import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useCombobox } from 'downshift-v9';
import vaDebounce from 'platform/utilities/data/debounce';
import PropTypes from 'prop-types';
import Autosuggest from 'platform/site-wide/autosuggest';
import { MIN_SEARCH_CHARS } from 'platform/site-wide/autosuggest/constants';
import UseMyLocation from './UseMyLocation';
import AddressInputError from './AddressInputError';
import { searchAddresses } from '../../../utils/mapHelpers';
import debounceWithFlush from '../../../utils/debounceWithFlush';

const onlySpaces = str => /^\s+$/.test(str);

function AddressAutosuggest({
  currentQuery,
  geolocateUser,
  inputRef,
  isMobile,
  isSmallDesktop,
  isTablet,
  onClearClick,
  onChange,
  onLocationSelection,
}) {
  const {
    locationChanged,
    searchString,
    geolocationInProgress,
    submitErrorLocation,
    submitErrorLocationInvalid,
    submitErrorFacilityType,
    submitErrorServiceType,
    invalidLocation,
  } = currentQuery;
  // Local copy of the controlled value: redux `searchString` is dispatched on a
  // debounce, so binding directly would revert the input mid-typing. Synced from
  // redux when external sources change it (URL params, geolocation, clear).
  const [inputValue, setInputValue] = useState(searchString || '');
  const [options, setOptions] = useState([]);
  // Local mirror of redux `invalidLocation` so the error clears as soon as the
  // user types again (the redux flag only clears on the next submit).
  const [geocodeInvalid, setGeocodeInvalid] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);

  // Tracks the last term we actually fetched results for, so the debounced
  // updateSearch can skip redundant fetches without depending on searchString
  // (which would recreate the callback and reset the debounce timer).
  const lastFetchedTermRef = useRef('');
  // Tracks whether the current invalidLocation=true streak has already been
  // handled, so fixing a facility/service error afterward (which changes the
  // effect's other deps while invalidLocation stays true) doesn't re-run the
  // focus logic — only the actual false→true transition should (CMS #24836).
  const invalidLocationHandledRef = useRef(false);
  // Mirrors the term currently in the input (trimStart-normalized, like the
  // fetched term). A searchAddresses call can resolve after the user has
  // cleared, edited, or picked something else; comparing against this lets the
  // resolver drop those stale responses instead of caching an unrelated feature
  // or resurrecting lastFetchedTermRef (CMS #20560).
  const latestTermRef = useRef(searchString || '');

  useEffect(
    () => {
      // Pull external changes (URL params, geolocation, clear actions) into
      // the local state. No-op when redux already matches local typing.
      if ((searchString || '') !== inputValue) {
        setInputValue(searchString || '');
        // An external source replaced the term, so a fetch still in flight for
        // the old one is stale (CMS #20560).
        latestTermRef.current = searchString || '';
      }
    },
    // intentionally only watching searchString — we don't want this to fire
    // for every local-state edit
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchString],
  );

  const errorID = 'street-city-state-zip-error';

  /**
   * updateSearch
   * @param {string} term
   * @returns {void}
   * updateSearch is not called directly but debounced below
   */
  const updateSearch = useCallback(
    term => {
      const trimmedTerm = term?.trimStart();
      if (trimmedTerm === lastFetchedTermRef.current) {
        return; // already fetched for this term
      }
      if (trimmedTerm.length >= MIN_SEARCH_CHARS) {
        setIsGeocoding(true);
        searchAddresses(trimmedTerm)
          .then(features => {
            // Drop responses for a term the user has since moved away from (an
            // in-flight fetch resolving after a clear, edit, or selection), so
            // we neither cache a stale feature nor mark it as fetched.
            if (trimmedTerm !== latestTermRef.current) {
              setIsGeocoding(false);
              return;
            }
            lastFetchedTermRef.current = trimmedTerm;
            if (!features?.length) {
              setOptions([
                {
                  id: 'null',
                  statusRole: 'status',
                  toDisplay: 'No results found',
                  disabled: true,
                },
              ]);
              // Nothing matches this term — drop any cached feature so submit
              // re-geocodes rather than searching an unrelated place.
              onLocationSelection?.({ locationGeocode: null });
            } else {
              setOptions([
                ...features.map(feature => ({
                  ...feature,
                  toDisplay: feature.place_name || trimmedTerm,
                })),
              ]);
              // Cache the first suggestion, keyed to the exact term, so a submit
              // without an explicit pick reuses it instead of firing a second
              // Mapbox request. useSearchSubmit only reuses it while the stored
              // term still matches the submitted string (CMS #20560).
              onLocationSelection?.({
                locationGeocode: { query: trimmedTerm, feature: features[0] },
              });
            }
            setIsGeocoding(false);
          })
          .catch(() => {
            onChange({ error: true });
            setIsGeocoding(false);
          });
      }
    },
    // searchString accessed via lastFetchedTermRef to keep this callback stable
    [onChange, onLocationSelection],
  );

  const debouncedUpdateSearch = useMemo(
    () => vaDebounce(500, updateSearch),
    [updateSearch],
  );

  // Debounced dispatch of the typed value to redux. Without this, every
  // keystroke would dispatch and re-render every consumer of currentQuery
  // (Mapbox, results list, etc.). The clear/no-input paths below still
  // dispatch synchronously so stale state doesn't linger; flushPendingDispatch
  // is wired up to onBlur so the latest value lands in Redux before the user
  // clicks the search button.
  const debouncedLocationSelection = useMemo(
    () =>
      onLocationSelection ? debounceWithFlush(150, onLocationSelection) : null,
    [onLocationSelection],
  );

  const flushPendingDispatch = useCallback(
    () => debouncedLocationSelection?.flush?.(),
    [debouncedLocationSelection],
  );

  const handleBlur = useCallback(() => {
    flushPendingDispatch();
    debouncedUpdateSearch.cancel?.();
  }, [flushPendingDispatch, debouncedUpdateSearch]);

  const handleOnSelect = event => {
    const { selectedItem: item } = event;
    if (!item || item.disabled) {
      return;
    }
    const displayValue = onlySpaces(item.toDisplay)
      ? ''
      : item.toDisplay.trim();
    setInputValue(displayValue);
    // The input now reflects the picked suggestion, so an earlier in-flight
    // fetch must not overwrite it when it resolves.
    latestTermRef.current = displayValue;
    // Downshift's selection pushes the place name back through the input, which
    // would otherwise schedule a suggestions fetch for a dropdown the user just
    // closed. Cancel it and record the term as already fetched — we have its
    // feature in hand, so that request has nothing left to learn (CMS #20560).
    debouncedUpdateSearch.cancel?.();
    lastFetchedTermRef.current = displayValue;
    // User picked a suggestion — cancel any pending debounced dispatch and
    // dispatch the chosen value synchronously so the form has the right
    // searchString immediately. The picked feature rides along so the search
    // lands on that exact place instead of re-geocoding the string (CMS #20560).
    debouncedLocationSelection?.cancel?.();
    onLocationSelection?.({
      searchString: displayValue,
      locationGeocode: displayValue
        ? { query: displayValue, feature: item }
        : null,
    });
  };

  useEffect(() => {
    return () => {
      debouncedUpdateSearch.cancel?.();
      debouncedLocationSelection?.cancel?.();
    };
  }, [debouncedUpdateSearch, debouncedLocationSelection]);

  const inputClearClick = useCallback(() => {
    debouncedUpdateSearch.cancel?.();
    debouncedLocationSelection?.cancel?.();
    onClearClick();
    onChange({ searchString: '' });
    setInputValue('');
    setOptions([]);
    setGeocodeInvalid(false);
    lastFetchedTermRef.current = '';
    latestTermRef.current = '';
  }, [
    onClearClick,
    onChange,
    debouncedUpdateSearch,
    debouncedLocationSelection,
  ]);

  const handleInputChange = e => {
    const { inputValue: value } = e;
    const trimmedValue = value?.trimStart() || '';
    setGeocodeInvalid(false);

    if (
      e.type === useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem
    ) {
      // selectedItem is hardcoded to null in Autosuggest to allow reselection.
      // When downshift reconciles back to null after a selection, ignore it —
      // handleOnSelect already set the correct value in draft state.
      return;
    }

    setInputValue(value || '');
    // Track the live term so a fetch resolving after this edit is discarded.
    latestTermRef.current = trimmedValue;

    // Clear stale results so old options don't linger during the pending fetch.
    if (trimmedValue !== lastFetchedTermRef.current) {
      setOptions([]);
    }

    if (!trimmedValue) {
      lastFetchedTermRef.current = '';
      // Cancel any pending debounced dispatch and dispatch the empty value
      // synchronously. This keeps draft `prev.searchString` in sync with
      // local input — otherwise the follow-up onClearClick's updateDraftState
      // would compare against the previously-flushed value and incorrectly
      // flip locationChanged to true (showing a phantom validation error).
      debouncedLocationSelection?.cancel?.();
      onLocationSelection?.({ searchString: '', locationGeocode: null });
      onClearClick();
      return;
    }

    debouncedLocationSelection?.({ searchString: trimmedValue });
    debouncedUpdateSearch(value);
  };

  useEffect(() => {
    if (!invalidLocation) {
      // Reset so the next false→true transition (a fresh background check)
      // is treated as new and re-evaluated.
      invalidLocationHandledRef.current = false;
      return;
    }
    if (invalidLocationHandledRef.current) {
      // Already handled this streak — a facility/service error flag changing
      // while invalidLocation stays true must not re-trigger focus (CMS #24836).
      return;
    }
    invalidLocationHandledRef.current = true;
    setGeocodeInvalid(true);
    // Focus the field so the error announces on the async geocode-failure
    // path (the synchronous submit path focuses it itself) (CMS #22751).
    // Skipped when a facility/service error is also active — that error was
    // focused first on submit, and the location result can arrive after the
    // user has already moved on to fix it; don't yank focus away from a
    // field they're actively working on (CMS #24836).
    if (!submitErrorFacilityType && !submitErrorServiceType) {
      document.getElementById('street-city-state-zip')?.focus();
    }
  }, [invalidLocation, submitErrorFacilityType, submitErrorServiceType]);

  // Computed during render (not in an effect) so aria-invalid is in the DOM in
  // the same commit the error appears — before the submit focus shift — which
  // is what lets JAWS announce on programmatic focus (CMS #22751).
  const showInvalid = submitErrorLocationInvalid || geocodeInvalid;
  const showEmpty =
    submitErrorLocation ||
    (locationChanged && !geolocationInProgress && !searchString?.length);
  const errorType = showInvalid ? 'invalid' : 'empty';
  const showAddressError = showInvalid || showEmpty;

  return (
    <Autosuggest
      inputValue={inputValue}
      onInputValueChange={handleInputChange}
      handleOnSelect={handleOnSelect}
      label={
        <>
          <span id="city-state-zip-text">
            Enter a zip code or a city and state
          </span>{' '}
          <span className="form-required-span">(*Required)</span>
        </>
      }
      options={options}
      downshiftInputProps={{
        id: 'street-city-state-zip',
        onBlur: handleBlur,
        disabled: false,
        autoCorrect: 'off',
        spellCheck: 'false',
        // Error text is announced by the live region in AddressInputError, not
        // aria-describedby — pairing both made VoiceOver read it twice (#22751).
        'aria-invalid': showAddressError ? 'true' : 'false',
      }}
      onClearClick={inputClearClick}
      clearButtonLabel="Clear location"
      inputError={
        <AddressInputError
          showError={showAddressError || false}
          errorId={errorID}
          errorType={errorType}
        />
      }
      showError={showAddressError}
      inputId="street-city-state-zip"
      inputRef={inputRef}
      labelSibling={
        <UseMyLocation
          onClick={geolocateUser}
          geolocationInProgress={currentQuery.geolocationInProgress}
          isSmallDesktop={isSmallDesktop}
          isTablet={isTablet}
          isMobile={isMobile}
        />
      }
      keepDataOnBlur
      showDownCaret={false}
      isLoading={isGeocoding}
      loadingMessage="Searching..."
      showOptionsRestriction
    />
  );
}

AddressAutosuggest.propTypes = {
  onChange: PropTypes.func.isRequired,
  currentQuery: PropTypes.shape({
    geolocationInProgress: PropTypes.bool,
    invalidLocation: PropTypes.bool,
    locationChanged: PropTypes.bool,
    searchString: PropTypes.string,
    submitErrorLocation: PropTypes.bool,
    submitErrorLocationInvalid: PropTypes.bool,
    submitErrorFacilityType: PropTypes.bool,
    submitErrorServiceType: PropTypes.bool,
  }),
  geolocateUser: PropTypes.func,
  inputRef: PropTypes.object,
  isMobile: PropTypes.bool,
  isSmallDesktop: PropTypes.bool,
  isTablet: PropTypes.bool,
  onClearClick: PropTypes.func,
  onLocationSelection: PropTypes.func,
};

export default AddressAutosuggest;
